import React, { useContext } from 'react';

import { Global, css } from '@emotion/react';

import ThemeModeContext from '../theme-provider/theme-mode-context';
import LayoutContext from '../context/layout-context';
import type { Lucid } from '../../types/types';

type Font = {
  name: string;
  type: string;
  family: string;
  weight: string;
  style?: string;
  folder: string;
  format?: string;
  tech?: string;
};

const jaFonts: Font[] = [
  {
    name: 'NotoSansJP-Variable',
    type: 'ttf',
    family: 'Noto Sans JP',
    weight: '100 900',
    folder: 'noto-sans-jp',
    format: 'truetype',
    tech: 'variations',
  },
];

const defaultfonts: Font[] = [
  {
    name: 'Graphik-SuperItalic-Cy-Web',
    type: 'woff2',
    family: 'Graphik',
    weight: '900',
    style: 'italic',
    folder: 'graphik',
  },
  { name: 'Graphik-Super-Cy-Web', type: 'woff2', family: 'Graphik', weight: '900', folder: 'graphik' },
  { name: 'Graphik-Bold-Cy-Web', type: 'woff2', family: 'Graphik', weight: '700', folder: 'graphik' },
  { name: 'Graphik-Light-Cy-Web', type: 'woff2', family: 'Graphik', weight: '300', folder: 'graphik' },
  { name: 'Graphik-Medium-Cy-Web', type: 'woff2', family: 'Graphik', weight: '500', folder: 'graphik' },
  { name: 'Graphik-Regular-Cy-Web', type: 'woff2', family: 'Graphik', weight: '400', folder: 'graphik' },
  {
    name: 'Graphik-RegularItalic-Cy-Web',
    type: 'woff2',
    family: 'Graphik',
    weight: '400',
    style: 'italic',
    folder: 'graphik',
  },
];

const fontUrlBase = 'https://cdn-cashy-static-assets.lucidchart.com/fonts/';

export const declareFontsCss = (shortCode: Lucid.I18n.LanguagesShortCode): string => {
  const fonts = [...defaultfonts, ...(shortCode === 'ja' ? jaFonts : [])];

  return fonts.reduce(
    (string, { name, type, family, weight, style, folder, format, tech }) => `${string}
  @font-face {
    font-family: '${family}';
    src: url('${fontUrlBase}${folder}/${name}.${type}') format('${format ?? type}')${tech ? ` tech(${tech})` : ''};
    font-weight: ${weight};
    font-display: auto;
    ${style ? `font-style: ${style}` : ''}
  }`,
    '',
  );
};

const GlobalCSS = () => {
  const { shortCode } = useContext(LayoutContext);
  const modeValue = useContext(ThemeModeContext);
  const isDarkMode = modeValue === 'dark';

  return (
    <Global
      styles={css([
        {
          'html, body, input, textarea, button': {
            fontFamily:
              'Graphik, "Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
          },
          'html, body': {
            margin: 0,
            padding: 0,
          },
          body: {
            backgroundColor: isDarkMode ? 'black' : 'white',
          },
          'ul, ol': {
            padding: 0,
            marginLeft: '20px',
          },
          '*, *::before, *::after': {
            boxSizing: 'border-box',
          },
          img: {
            maxWidth: '100%',
          },
          fieldset: {
            border: 'none',
          },
          // This is a fix for Osano
          '.osano-cm-widget': {
            display: 'none',
          },
          // This is a fix for the Qualified chatbot to keep it from covering the footer
          'iframe#q-messenger-frame:not(.qlfd-is-mobile.maximized)': {
            bottom: '50px !important',
          },
        },
        declareFontsCss(shortCode),
      ])}
    />
  );
};

export default GlobalCSS;
